import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  Box,
  Typography,
  Button,
  Select,
  Option,
  CircularProgress,
  Alert,
  Sheet,
  Card,
  CardContent,
  Checkbox,
  Tooltip,
  IconButton as JoyIconButton
} from '@mui/joy';
import {
  ArrowsClockwise as RefreshCw,
  Plus,
  TrendUp as TrendingUp,
  CurrencyDollar as DollarSign,
  Target,
  Users,
  Info,
  GridFour as LayoutGrid,
  List as ListIcon,
} from '@phosphor-icons/react';
import type { Opportunity, OpportunityBoard as OpportunityBoardType, OpportunityBoardStatus } from '../../types';
import { OpportunityKanbanColumn } from '../shared';
import { OpportunityCard } from './OpportunityCard';
import { useOpportunitiesKanban, useAllOpportunities } from '../../hooks/useOpportunities';
import { OpportunityDropFooter } from './OpportunityDropFooter';
import { LostReasonModal } from './LostReasonModal';
import { WonConfirmModal } from './WonConfirmModal';
import OpportunityModal from './opportunities/OpportunityModal';
import { api } from '../../lib/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OpportunityListView } from './OpportunityListView';

const MOBILE_QUERY = '(max-width: 959.95px)';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

type ViewMode = 'board' | 'list';

interface OpportunityBoardProps {
  // Optional props for when used as a controlled component
  opportunities?: Opportunity[];
  boards?: OpportunityBoardType[];
  statuses?: OpportunityBoardStatus[];
  loading?: { isLoading: boolean; error: string | null };
  selectedBoard?: number;
  onBoardChange?: (boardId: number) => void;
  onUpdateOpportunityStatus?: (opportunityId: number, statusId: number) => Promise<void>;
  onDeleteOpportunity?: (opportunityId: number) => Promise<void>;
  onRefresh?: () => void;
  onAddOpportunity?: (statusId?: number) => void;
  onEditOpportunity?: (opportunity: Opportunity) => void;
}

export const OpportunityBoard: React.FC<OpportunityBoardProps> = ({
  opportunities: propOpportunities,
  boards: propBoards,
  statuses: propStatuses,
  loading: propLoading,
  selectedBoard: propSelectedBoard,
  onBoardChange: propOnBoardChange,
  onUpdateOpportunityStatus: propOnUpdateOpportunityStatus,
  onDeleteOpportunity: propOnDeleteOpportunity,
  onRefresh: propOnRefresh,
  onAddOpportunity,
  onEditOpportunity,
}) => {
  // Use the kanban hook if no props are provided (uncontrolled mode)
  const kanbanData = useOpportunitiesKanban();
  
  // Fetch all opportunities for win rate calculation
  const { data: allOpportunitiesData } = useAllOpportunities();
  // Ensure allOpportunities is always an array
  const allOpportunities = Array.isArray(allOpportunitiesData) ? allOpportunitiesData : [];
  
  // Use either prop data or hook data, ensuring they are arrays
  const opportunities = Array.isArray(propOpportunities) ? propOpportunities : 
                       Array.isArray(kanbanData.opportunities) ? kanbanData.opportunities : [];
  const boards = Array.isArray(propBoards) ? propBoards : 
                Array.isArray(kanbanData.boards) ? kanbanData.boards : [];
  const statuses = Array.isArray(propStatuses) ? propStatuses : 
                  Array.isArray(kanbanData.statuses) ? kanbanData.statuses : [];
  
  // Debug logging
  console.log('[OpportunityBoard] Data check:', {
    opportunities: Array.isArray(opportunities) ? `Array(${opportunities.length})` : typeof opportunities,
    boards: Array.isArray(boards) ? `Array(${boards.length})` : typeof boards,
    statuses: Array.isArray(statuses) ? `Array(${statuses.length})` : typeof statuses,
    allOpportunities: Array.isArray(allOpportunities) ? `Array(${allOpportunities.length})` : typeof allOpportunities
  });
  const loading = propLoading || kanbanData.loading;
  const selectedBoard = propSelectedBoard || kanbanData.selectedBoard;
  const onBoardChange = propOnBoardChange || kanbanData.setSelectedBoard;
  const onUpdateOpportunityStatus = propOnUpdateOpportunityStatus || kanbanData.updateOpportunityStatus;
  const onDeleteOpportunity = propOnDeleteOpportunity || kanbanData.deleteOpportunity;
  const onRefresh = propOnRefresh || kanbanData.refreshData;
  const [activeOpportunity, setActiveOpportunity] = useState<Opportunity | null>(null);
  const [pendingWonOpportunity, setPendingWonOpportunity] = useState<Opportunity | null>(null);
  const [pendingLostOpportunity, setPendingLostOpportunity] = useState<Opportunity | null>(null);
  const [showWon, setShowWon] = useState(false);
  const [showLost, setShowLost] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [newOpportunityStatusId, setNewOpportunityStatusId] = useState<number | undefined>(undefined);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // View mode (board / list). On mobile we default to list to avoid accidental drag-drop;
  // on desktop we default to board. URL param ?view=list|board overrides the default.
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get('view') as ViewMode | null;
  const viewMode: ViewMode =
    urlView === 'list' || urlView === 'board' ? urlView : isMobile ? 'list' : 'board';
  const setViewMode = (next: ViewMode) => {
    const params = new URLSearchParams(searchParams);
    params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  // Add keyboard navigation for horizontal scrolling
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!scrollContainerRef.current) return;
      
      const scrollAmount = 320; // Width of one column
      
      if (e.key === 'ArrowLeft' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: -scrollAmount, behavior: 'auto' });
          }
        });
      } else if (e.key === 'ArrowRight' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'auto' });
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Add horizontal scroll with mouse wheel (when holding shift)
  React.useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!scrollContainerRef.current) return;
      
      // Horizontal scroll when shift is held or when scroll is horizontal
      if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        e.preventDefault();
        const scrollAmount = e.shiftKey ? e.deltaY : e.deltaX;
        
        // Use requestAnimationFrame for smoother scrolling
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'auto' });
          }
        });
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, []); // Remove statuses dependency to prevent unnecessary re-attachments

  // Mobile devices skip the PointerSensor entirely so a touch drag can never accidentally
  // reclassify an opportunity. List view is the recommended way to move stages on mobile.
  const desktopSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(...(isMobile ? [] : [desktopSensor]));

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    // Find from original opportunities list since we might be dragging a closed item
    const opportunity = opportunities.find(opp => opp.id === active.id);
    setActiveOpportunity(opportunity || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveOpportunity(null);

    if (!over) return;

    const activeOpportunityId = active.id as number;
    const overId = over.id as string;
    const opportunity = opportunities.find(opp => opp.id === activeOpportunityId);

    if (!opportunity) return;

    // Check if dropped on Won/Lost zones
    if (overId === 'opportunity-won') {
      setPendingWonOpportunity(opportunity);
      return;
    }

    if (overId === 'opportunity-lost') {
      setPendingLostOpportunity(opportunity);
      return;
    }

    // Determine the target status ID
    let targetStatusId: number;

    // Check if dropped on a column (overId starts with 'column-')
    if (overId.toString().startsWith('column-')) {
      targetStatusId = parseInt(overId.replace('column-', ''));
    } else {
      // Dropped on another opportunity card - find which column it belongs to
      const targetOpportunity = opportunities.find(opp => opp.id === parseInt(overId.toString()));
      if (targetOpportunity && targetOpportunity.board_status) {
        targetStatusId = targetOpportunity.board_status;
      } else {
        // Can't determine target column
        return;
      }
    }

    try {
      await onUpdateOpportunityStatus(activeOpportunityId, targetStatusId);
    } catch (error) {
      // TODO: Show error notification
    }
  };

  const handleAddOpportunity = (statusId?: number) => {
    if (onAddOpportunity) {
      onAddOpportunity(statusId);
    } else {
      // Default implementation: open modal
      setNewOpportunityStatusId(statusId);
      setEditingOpportunity(null);
      setShowOpportunityModal(true);
    }
  };

  const handleEditOpportunity = (opportunity: Opportunity) => {
    if (onEditOpportunity) {
      onEditOpportunity(opportunity);
    } else {
      // Default implementation: open modal with opportunity data
      setEditingOpportunity(opportunity);
      setNewOpportunityStatusId(undefined);
      setShowOpportunityModal(true);
    }
  };

  const handleSaveOpportunity = async (opportunityData: Partial<Opportunity>) => {
    try {
      const isUpdate = '_isUpdate' in opportunityData && opportunityData._isUpdate;

      if (isUpdate && opportunityData.id) {
        // Update existing opportunity
        await api.updateOpportunity(opportunityData.id, opportunityData);
      } else {
        // Create new opportunity
        await api.createOpportunity(opportunityData);
      }

      // Refresh data and close modal
      await onRefresh();
      setShowOpportunityModal(false);
      setEditingOpportunity(null);
      setNewOpportunityStatusId(undefined);
    } catch (error) {
      console.error('Failed to save opportunity:', error);
      throw error;
    }
  };

  const handleCloseOpportunityModal = () => {
    setShowOpportunityModal(false);
    setEditingOpportunity(null);
    setNewOpportunityStatusId(undefined);
  };

  const handleDeleteOpportunity = async (opportunityId: number) => {
    if (window.confirm('Are you sure you want to delete this opportunity?')) {
      try {
        await onDeleteOpportunity(opportunityId);
      } catch (error) {
        // TODO: Show error notification
      }
    }
  };

  // Filter opportunities based on show won/lost settings
  const filteredOpportunities = React.useMemo(() => {
    const filtered = opportunities.filter(opp => {
      // If opportunity is won and showWon is false, hide it
      if (opp.is_closed && opp.is_won && !showWon) {
        return false;
      }
      // If opportunity is lost and showLost is false, hide it
      if (opp.is_closed && !opp.is_won && !showLost) {
        return false;
      }
      // Otherwise, show open opportunities and those matching the filters
      return true;
    });
    
    return filtered;
  }, [opportunities, showWon, showLost]);

  // Filter to only open opportunities for metrics calculation (for current board only)
  const openOpportunities = React.useMemo(() => {
    const open = opportunities.filter(opp => {
      // Skip if opportunity doesn't have a board when board filtering is active
      if (selectedBoard && !opp.board) {
        return false;
      }
      
      // CRITICAL: Skip opportunities without a board_status - they're not visible in any column!
      if (!opp.board_status && opp.board_status !== 0) {
        return false;
      }
      
      // Ensure proper type comparison for board
      const oppBoard = typeof opp.board === 'string' ? parseInt(opp.board, 10) : (opp.board || 0);
      const currentBoard = typeof selectedBoard === 'string' ? parseInt(selectedBoard, 10) : (selectedBoard || 0);
      
      // Only filter by board if a board is selected
      const isFromCurrentBoard = !selectedBoard || oppBoard === currentBoard;
      const isOpen = !opp.is_closed;
      
      return isFromCurrentBoard && isOpen && (opp.board_status || opp.board_status === 0);
    });
    
    return open;
  }, [opportunities, selectedBoard]);

  // Calculate pipeline metrics in a dedicated useMemo for better tracking
  const pipelineMetrics = React.useMemo(() => {
    const metrics = {
      totalValue: 0,
      weightedValue: 0,
      avgCloseRate: 0,
      count: openOpportunities.length
    };
    
    // Calculate total value and weighted value from open opportunities
    if (openOpportunities.length > 0) {
      // Calculate total value
      metrics.totalValue = openOpportunities.reduce((sum, opp) => {
        const value = typeof opp.estimated_value === 'string' 
          ? parseFloat(opp.estimated_value) 
          : parseFloat(String(opp.estimated_value || 0));
        const numValue = isNaN(value) ? 0 : value;
        
        return sum + numValue;
      }, 0);
      
      // Calculate weighted value
      metrics.weightedValue = openOpportunities.reduce((sum, opp) => {
        const value = typeof opp.estimated_value === 'string' 
          ? parseFloat(opp.estimated_value) 
          : parseFloat(String(opp.estimated_value || 0));
        const numValue = isNaN(value) ? 0 : value;
        return sum + (numValue * opp.probability / 100);
      }, 0);
    }
    
    // Calculate average close rate based on ALL closed opportunities (won vs lost)
    // This looks at historical data across the entire organization
    const safeAllOpportunities = Array.isArray(allOpportunities) ? allOpportunities : [];
    const closedOpportunities = safeAllOpportunities.filter(opp => opp.is_closed === true);
    const wonOpportunities = closedOpportunities.filter(opp => opp.is_won === true);
    
    if (closedOpportunities.length > 0) {
      metrics.avgCloseRate = Math.round((wonOpportunities.length / closedOpportunities.length) * 100);
    } else {
      metrics.avgCloseRate = 0;
    }
    
    return metrics;
  }, [openOpportunities, allOpportunities]);

  const getOpportunitiesByStatus = (statusId: number) => {
    const filtered = filteredOpportunities.filter(opp => opp.board_status === statusId);
    
    return filtered;
  };

  const handleWonConfirm = async (actualValue: number, notes: string, boardId: number, statusId: number, jobName: string) => {
    if (!pendingWonOpportunity) return;

    try {
      // Persist the final contract value first - convert_to_job copies the opportunity's
      // estimated_value onto the new job, so updating afterwards would stamp the stale figure.
      if (actualValue !== pendingWonOpportunity.estimated_value) {
        await api.updateOpportunity(pendingWonOpportunity.id, {
          estimated_value: actualValue,
        });
      }

      // Keep the win notes as an opportunity note (also written before the conversion closes it)
      if (notes.trim()) {
        await api.post('/api/crm/opportunity-notes/', {
          opportunity: pendingWonOpportunity.id,
          content: notes.trim(),
          note_type: 'general',
          is_private: false,
        });
      }

      // Convert to job while the opportunity is still open
      const response = await api.convertOpportunityToJob(pendingWonOpportunity.id, boardId, statusId, jobName);

      if (response.data.success) {
        // Don't refresh here since we're navigating away
        // The navigation will happen immediately
        
        // Navigate to the new job (use window.location since we're going to a different app)
        window.location.href = `/operations/jobs/${response.data.job_id}`;
      }
    } catch (error: any) {
      // TODO: Show error notification
      alert(`Failed to create job: ${error.message || 'Unknown error'}`);
    } finally {
      setPendingWonOpportunity(null);
    }
  };

  const handleLostConfirm = async (reason: string, notes: string) => {
    if (!pendingLostOpportunity) return;

    try {
      // Update opportunity to closed lost
      // Note: Backend sets is_closed and is_won based on stage field
      const response = await api.updateOpportunity(pendingLostOpportunity.id, {
        stage: 'closed_lost',  // This will trigger is_closed=true, is_won=false
        loss_reason: reason,
        loss_reason_details: notes || '',
      });

      // Refresh data to ensure we have the latest state
      await onRefresh();
    } catch (error) {
      // TODO: Show error notification
    } finally {
      setPendingLostOpportunity(null);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading.isLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" textColor="text.secondary">
          Loading sales pipeline...
        </Typography>
      </Box>
    );
  }

  if (loading.error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Alert color="danger" size="lg">
          <Typography level="title-md">Error loading pipeline</Typography>
          <Typography level="body-sm">{loading.error}</Typography>
        </Alert>
        <Button
          onClick={onRefresh}
          color="primary"
          variant="solid"
          startDecorator={<RefreshCw size={16} />}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Sheet
        variant="outlined"
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderRadius: 0,
          borderTop: 'none',
          borderLeft: 'none',
          borderRight: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Page title comes from the Sales shell — this is the view label */}
            <Typography level="title-lg">
              {viewMode === 'list' ? 'Pipeline' : 'Opportunity Board'}
            </Typography>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography level="body-sm" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                    Navigation Tips:
                  </Typography>
                  <Typography level="body-xs" component="div">
                    • Scroll normally to move up/down
                  </Typography>
                  <Typography level="body-xs" component="div">
                    • Hold <strong>Shift + Scroll</strong> to move left/right
                  </Typography>
                  <Typography level="body-xs" component="div">
                    • Use <strong>Cmd/Ctrl + Arrow Keys</strong> for quick navigation
                  </Typography>
                </Box>
              }
              placement="right"
              variant="outlined"
            >
              <JoyIconButton size="sm" variant="plain" color="neutral">
                <Info size={16} />
              </JoyIconButton>
            </Tooltip>
            {boards.length > 0 && (
              <Select
                value={selectedBoard?.toString() || ''}
                onChange={(_, value) => {
                  if (value) {
                    const boardId = parseInt(value as string);
                    onBoardChange(boardId);
                  }
                }}
                placeholder="Select board"
                size="sm"
              >
                {boards.map(board => (
                  <Option key={board.id} value={board.id.toString()}>
                    {board.name}
                  </Option>
                ))}
              </Select>
            )}
            <Checkbox
              checked={showWon}
              onChange={(e) => setShowWon(e.target.checked)}
              label="Show Won"
              size="sm"
            />
            <Checkbox
              checked={showLost}
              onChange={(e) => setShowLost(e.target.checked)}
              label="Show Lost"
              size="sm"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View toggle (Board / List) */}
            <Sheet
              variant="outlined"
              sx={{
                display: 'inline-flex',
                borderRadius: 'sm',
                p: 0.25,
                gap: 0.25,
                bgcolor: 'background.level1',
              }}
            >
              <Button
                size="sm"
                variant={viewMode === 'board' ? 'solid' : 'plain'}
                color={viewMode === 'board' ? 'primary' : 'neutral'}
                onClick={() => setViewMode('board')}
                startDecorator={<LayoutGrid size={14} />}
              >
                Board
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'solid' : 'plain'}
                color={viewMode === 'list' ? 'primary' : 'neutral'}
                onClick={() => setViewMode('list')}
                startDecorator={<ListIcon size={14} />}
              >
                List
              </Button>
            </Sheet>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                onRefresh();
              }}
              disabled={loading.isLoading}
              startDecorator={<RefreshCw size={16} className={loading.isLoading ? 'animate-spin' : ''} />}
            >
              Refresh
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={() => handleAddOpportunity()}
              startDecorator={<Plus size={16} />}
            >
              Add Opportunity
            </Button>
          </Box>
        </Box>

        {/* Pipeline Metrics */}
        {opportunities.length > 0 && selectedBoard && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: { xs: 1, md: 2 },
            }}
          >
            <Card variant="soft" color="primary" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DollarSign size={16} />
                  <Box>
                    <Typography level="body-xs" textColor="text.secondary">
                      Total Pipeline Value
                    </Typography>
                    <Typography level="title-lg" fontWeight="lg">
                      {formatCurrency(pipelineMetrics.totalValue)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card variant="soft" color="success" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp size={16} />
                  <Box>
                    <Typography level="body-xs" textColor="text.secondary">
                      Weighted Value
                    </Typography>
                    <Typography level="title-lg" fontWeight="lg">
                      {formatCurrency(pipelineMetrics.weightedValue)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card variant="soft" color="warning" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Target size={16} />
                  <Box>
                    <Typography level="body-xs" textColor="text.secondary">
                      Historical Win Rate
                    </Typography>
                    <Typography level="title-lg" fontWeight="lg">
                      {pipelineMetrics.avgCloseRate}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card variant="soft" color="neutral" sx={{ flex: 1 }}>
              <CardContent sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Users size={16} />
                  <Box>
                    <Typography level="body-xs" textColor="text.secondary">
                      Total Opportunities
                    </Typography>
                    <Typography level="title-lg" fontWeight="lg">
                      {pipelineMetrics.count}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Sheet>

      {/* Pipeline Board / List */}
      <Box
        sx={{
          flex: 1,
          overflow: viewMode === 'list' ? 'auto' : 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {statuses.length === 0 ? (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 2
          }}>
            <Typography level="h4" textColor="text.secondary">
              No board selected or no statuses available
            </Typography>
          </Box>
        ) : viewMode === 'list' ? (
          <OpportunityListView
            opportunities={filteredOpportunities}
            statuses={statuses}
            onMoveToStatus={onUpdateOpportunityStatus}
            onMarkWon={(opp) => setPendingWonOpportunity(opp)}
            onMarkLost={(opp) => setPendingLostOpportunity(opp)}
            onEdit={handleEditOpportunity}
            onDelete={handleDeleteOpportunity}
          />
        ) : (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Box sx={{
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
              minHeight: 0 // Important for flex children with overflow
            }}>
              <div
                className="kanban-scroll-container"
                ref={scrollContainerRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  overflowX: 'auto',
                  overflowY: 'auto' // Enable vertical scrolling
                }}
              >
                <div
                  className="kanban-board"
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    padding: '1rem',
                    minHeight: 'calc(100vh - 300px)', // Ensure minimum height for the board
                    height: 'auto', // Allow board to expand
                    alignItems: 'stretch', // Stretch columns to same height
                    width: 'max-content'
                  }}
                >
                  {statuses
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((status) => (
                      <div
                        key={status.id}
                        className="kanban-column-wrapper"
                        style={{
                          flex: '0 0 360px', // Increased from 320px to 360px
                          minWidth: '360px',
                          minHeight: '600px', // Minimum height for each column
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <OpportunityKanbanColumn
                          status={status}
                          opportunities={getOpportunitiesByStatus(status.id)}
                          onAddOpportunity={handleAddOpportunity}
                          onEditOpportunity={handleEditOpportunity}
                          onDeleteOpportunity={handleDeleteOpportunity}
                        />
                      </div>
                    ))}
                </div>
              </div>
            </Box>

            <DragOverlay>
              {activeOpportunity ? (
                <OpportunityCard opportunity={activeOpportunity} />
              ) : null}
            </DragOverlay>
            {/* Drop Footer - only show when dragging */}
            <OpportunityDropFooter isActive={!!activeOpportunity} />
          </DndContext>
        )}
      </Box>

      {/* Won Confirmation Modal */}
      <WonConfirmModal
        open={!!pendingWonOpportunity}
        onClose={() => setPendingWonOpportunity(null)}
        onConfirm={handleWonConfirm}
        opportunityName={pendingWonOpportunity?.name || ''}
        estimatedValue={pendingWonOpportunity?.estimated_value || 0}
      />

      {/* Lost Reason Modal */}
      <LostReasonModal
        open={!!pendingLostOpportunity}
        onClose={() => setPendingLostOpportunity(null)}
        onConfirm={handleLostConfirm}
        opportunityName={pendingLostOpportunity?.name || ''}
      />

      {/* Opportunity Create/Edit Modal */}
      {showOpportunityModal && (
        <OpportunityModal
          open={showOpportunityModal}
          onClose={handleCloseOpportunityModal}
          contactId={editingOpportunity?.contact || 0}
          opportunity={editingOpportunity}
          boardId={selectedBoard || undefined}
          initialStatusId={newOpportunityStatusId}
          onSaveOpportunity={handleSaveOpportunity}
        />
      )}
    </Box>
  );
};